import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { patientOfflineStore } from '@/core/offline'
import { AccountType } from '@/core/models/account.model'
import { SubscriptionType } from '@/utils/subscription/types'
import { KeycloakUserType } from '@/core/types/shared'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'

export function useCreateOfflinePatient() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const onSubmit = async (values: PatientProfileFormData) => {
    try {
      const now = Date.now()
      const offlineId = `offline_${now}_${Math.random().toString(36).substr(2, 9)}`

      const offlinePatient = {
        _id: offlineId,
        _offlineId: offlineId,
        _localId: offlineId,
        _syncStatus: 'pending',
        _lastModified: now,
        _createdAt: now,
        // Account base fields
        accountType: AccountType.Patient,
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        email: values.email || '',
        licenses: [],
        permissions: [],
        policies: [],
        roles: [],
        subscription: SubscriptionType.NO_SUBSCRIPTION,
        type: KeycloakUserType.PATIENT,
        userInfo: {
          name: values.name || '',
          surname: values.surname || '',
          gender: values.gender || 'I',
          birthday: values.birthday ? new Date(values.birthday).toISOString() : undefined,
        },
        contactInfo: {
          phone: values.phone || undefined,
          street: values.street || undefined,
          city: values.city || undefined,
          cp: values.cp ? values.cp : undefined,
          country: values.country || undefined,
        },
        // Patient specific fields
        agreement: values.agreement || 'accepted',
        secu: values.secu || '',
        weight: values.weight ? parseInt(values.weight, 10) : undefined,
        height: values.height ? parseInt(values.height, 10) : undefined,
        allergy: values.allergy || undefined,
        vaccins: values.vaccins || undefined,
        notes: values.notes || undefined,
        background: values.background || undefined,
        treatment: values.treatment || undefined,
        codeGestion: values.codeGestion || undefined,
        codeRegime: values.codeRegime || undefined,
        caisse: values.caisse || undefined,
        consultations: [],
        parents:
          values.parents?.map((parent) => ({
            _id: `parent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            firstname: parent.firstname,
            lastname: parent.lastname,
            email: parent.email || undefined,
            phone: parent.phone || undefined,
            city: parent.city || undefined,
            cp: parent.cp ? parent.cp : undefined,
            street: undefined,
            patient: offlineId,
          })) || [],
        attendingPhysician:
          values.privateDoctor && values.privateDoctor.firstname && values.privateDoctor.lastname
            ? {
              firstname: values.privateDoctor.firstname,
              lastname: values.privateDoctor.lastname,
              email: values.privateDoctor.email || undefined,
              phone: values.privateDoctor.phone || undefined,
              city: values.privateDoctor.city || undefined,
              cp: values.privateDoctor.cp ? values.privateDoctor.cp : undefined,
              street: undefined,
            }
            : undefined,
        pluginId: values.pluginId || undefined,
      }

      const createdPatient = await patientOfflineStore.createOfflinePatient(offlinePatient)
      navigate(`/offline/patients/${createdPatient._offlineId}`)
    } catch (error) {
      console.error('Error creating offline patient:', error)
      // TODO: Add error handling UI
    }
  }

  return { onSubmit }
}
